import { useState, useCallback, useMemo, useRef, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api-client";
import { useContactList } from "@/hooks/use-contacts";
import {
  buildCanvasWorkflowPrompt,
  diffWorkflowVersions,
  parseGeneratedCanvasWorkflow,
  type WorkflowVersionDiff,
  type WorkflowVersionSnapshot,
} from "@ease/workflows";
import {
  useWorkflowVersions,
  restoreWorkflowVersion,
  type WorkflowNode,
  type WorkflowEdge,
  type WorkflowComplianceSummary,
  type WorkflowFrequencyCap,
  type WorkflowQuietHours,
  type WorkflowVersion,
} from "@/hooks/use-workflows";
import { NodePalette, NODE_TYPES } from "./node-palette";
import { NodeConfig } from "./node-config";
import { WorkflowCompliancePanel } from "./compliance-display";
import { Clock3, GitBranch, History, LayoutGrid, Search, Sparkles, UserRound, Waypoints, Code2 } from "lucide-react";

type EditorMode = "visual" | "ai" | "code";
type TestResultTone = "info" | "success" | "error";
type CanvasPoint = { x: number; y: number };

const NODE_WIDTH = 160;
const NODE_HEIGHT = 56;
const MIN_CANVAS_SCALE = 0.6;
const MAX_CANVAS_SCALE = 1.8;
const CANVAS_WHEEL_ZOOM_STEP = 0.0015;

interface WorkflowDryRunResponse {
  data: {
    estimatedPath: string[];
    estimatedDurationMs: number;
    estimatedSteps: Array<{
      nodeId: string;
      nodeType: string;
      actionRef?: string;
      estimatedDurationMs: number;
      wouldExecute: boolean;
      reason?: string;
    }>;
    branchDecisions: Array<{
      nodeId: string;
      condition: string;
      branch: "yes" | "no" | "unknown";
      reason: string;
    }>;
    warnings: string[];
  };
}

interface WorkflowBacktestResponse {
  data: {
    range: "7d" | "30d" | "90d";
    windowStart: string;
    totalEnrollments: number;
    triggeredEnrollments: number;
    completedEnrollments: number;
    nodeCounts: Array<{
      nodeId: string;
      nodeName: string;
      count: number;
    }>;
    branchCounts: Array<{
      nodeId: string;
      branches: Array<{
        targetNodeId: string;
        label: string;
        count: number;
      }>;
    }>;
  };
}

interface WorkflowAiGenerateResponse {
  content?: string;
}

interface WorkflowTestResult {
  tone: TestResultTone;
  message: string;
}

const WORKFLOW_DRY_RUN_CONTACT_ID = "workflow-editor-dry-run";

type PublishWorkflowVersion = (
  workflowId: string,
  nodes: WorkflowNode[],
  edges: WorkflowEdge[],
) => Promise<void>;

type RunWorkflowDryRun = (
  workflowId: string,
  contactId: string,
) => Promise<WorkflowDryRunResponse["data"]>;

type RunWorkflowBacktest = (
  workflowId: string,
  range: "7d" | "30d" | "90d",
) => Promise<WorkflowBacktestResponse["data"]>;

async function defaultPublishWorkflowVersion(
  workflowId: string,
  nodes: WorkflowNode[],
  edges: WorkflowEdge[],
) {
  await apiFetch(`/crm/workflows/${workflowId}/versions`, {
    method: "POST",
    body: JSON.stringify({ nodes, edges }),
  });
}

async function defaultRunWorkflowDryRun(
  workflowId: string,
  contactId: string,
): Promise<WorkflowDryRunResponse["data"]> {
  const response = await apiFetch<WorkflowDryRunResponse>(`/crm/workflows/${workflowId}/dry-run`, {
    method: "POST",
    body: JSON.stringify({
      contactId,
      triggerData: { source: "workflow_editor_dry_run" },
    }),
  });

  return response.data;
}

async function defaultRunWorkflowBacktest(
  workflowId: string,
  range: "7d" | "30d" | "90d",
): Promise<WorkflowBacktestResponse["data"]> {
  const response = await apiFetch<WorkflowBacktestResponse>(`/crm/workflows/${workflowId}/simulate`, {
    method: "POST",
    body: JSON.stringify({ range }),
  });

  return response.data;
}

function formatEstimatedDuration(durationMs: number): string {
  if (durationMs < 60_000) {
    return `${Math.max(1, Math.round(durationMs / 1000))}s`;
  }

  const roundedMinutes = Math.round(durationMs / 60_000);
  return `${Math.max(1, roundedMinutes)} min`;
}

function clampCanvasScale(scale: number): number {
  return Math.min(MAX_CANVAS_SCALE, Math.max(MIN_CANVAS_SCALE, scale));
}

function getCanvasPoint(
  clientX: number,
  clientY: number,
  rect: DOMRect,
  offset: CanvasPoint,
  scale: number,
): CanvasPoint {
  return {
    x: (clientX - rect.left - offset.x) / scale,
    y: (clientY - rect.top - offset.y) / scale,
  };
}

function buildBezierPath(source: CanvasPoint, target: CanvasPoint): string {
  const deltaX = Math.max(80, Math.abs(target.x - source.x) * 0.45);
  return `M ${source.x} ${source.y} C ${source.x + deltaX} ${source.y}, ${target.x - deltaX} ${target.y}, ${target.x} ${target.y}`;
}

export function computeDraggedNodePosition(
  nodeOrigin: CanvasPoint,
  pointerOrigin: CanvasPoint,
  pointerCurrent: CanvasPoint,
): CanvasPoint {
  return {
    x: Math.round(nodeOrigin.x + (pointerCurrent.x - pointerOrigin.x)),
    y: Math.round(nodeOrigin.y + (pointerCurrent.y - pointerOrigin.y)),
  };
}

type WorkflowVersionNodeWithActionRef = WorkflowVersion["nodes"][number] & { actionRef?: string };

function toWorkflowVersionSnapshot(version: WorkflowVersion): WorkflowVersionSnapshot | null {
  if (!Array.isArray(version.nodes) || !Array.isArray(version.edges)) {
    return null;
  }

  return {
    nodes: version.nodes.map((node) => ({
      id: node.id,
      type: node.type,
      actionRef: typeof (node as WorkflowVersionNodeWithActionRef).actionRef === "string"
        ? (node as WorkflowVersionNodeWithActionRef).actionRef ?? ""
        : "",
      config: node.config ?? {},
    })),
    edges: version.edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.label,
      condition: edge.condition,
    })),
  };
}

function formatVersionDelta(added: number, removed: number, changed: number): string {
  return `+${added} / -${removed} / ~${changed}`;
}

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

function buildProjectedOutcomeChanges(diff: WorkflowVersionDiff): string[] {
  const messages: string[] = [];

  if (diff.nodes.added.length > 0) {
    messages.push(`${pluralize(diff.nodes.added.length, "new step")} can expand reachable execution paths.`);
  }
  if (diff.nodes.removed.length > 0) {
    messages.push(`${pluralize(diff.nodes.removed.length, "removed step")} can shorten or bypass prior execution paths.`);
  }

  const typeChanges = diff.nodes.changed.filter((change) => change.changedFields.includes("type")).length;
  if (typeChanges > 0) {
    messages.push(`${pluralize(typeChanges, "step type")} changed, so execution intent may differ.`);
  }

  const actionChanges = diff.nodes.changed.filter((change) => change.changedFields.includes("actionRef")).length;
  if (actionChanges > 0) {
    messages.push(`${pluralize(actionChanges, "step action")} changed, so dispatched work may differ.`);
  }

  const configChanges = diff.nodes.changed.filter((change) => change.changedFields.includes("config")).length;
  if (configChanges > 0) {
    messages.push(`${pluralize(configChanges, "step configuration")} changed, so runtime behavior may differ.`);
  }

  const routingChanges =
    diff.edges.added.length +
    diff.edges.removed.length +
    diff.edges.changed.filter((change) =>
      change.changedFields.some((field) =>
        field === "source" || field === "target" || field === "label" || field === "condition",
      ),
    ).length;
  if (routingChanges > 0) {
    messages.push(`${pluralize(routingChanges, "connection change")} can alter branch routing.`);
  }

  return messages;
}

interface WorkflowEditorProps {
  workflowId: string;
  workflowName: string;
  initialNodes: WorkflowNode[];
  initialEdges: WorkflowEdge[];
  compliance?: WorkflowComplianceSummary | null;
  quietHours?: WorkflowQuietHours | null;
  frequencyCap?: WorkflowFrequencyCap | null;
  onBack: () => void;
  onPublish?: PublishWorkflowVersion;
  onDryRun?: RunWorkflowDryRun | null;
  onBacktest?: RunWorkflowBacktest | null;
}

/** Visual DAG editor — canvas with draggable nodes, connections, config panel. */
export function WorkflowEditor({
  workflowId,
  workflowName,
  initialNodes,
  initialEdges,
  compliance,
  quietHours,
  frequencyCap,
  onBack,
  onPublish,
  onDryRun,
  onBacktest,
}: WorkflowEditorProps) {
  const canvasViewportRef = useRef<HTMLDivElement | null>(null);
  const [nodes, setNodes] = useState<WorkflowNode[]>(initialNodes);
  const [edges, setEdges] = useState<WorkflowEdge[]>(initialEdges);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [connectingFrom, setConnectingFrom] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setDirty] = useState(false);
  const [isTestMode, setIsTestMode] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<WorkflowTestResult | null>(null);
  const [editorMode, setEditorMode] = useState<EditorMode>("visual");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [codeValue, setCodeValue] = useState("");
  const [showVersions, setShowVersions] = useState(false);
  const [restoringVersionId, setRestoringVersionId] = useState<string | null>(null);
  const [showSimulation, setShowSimulation] = useState(false);
  const [simulationResult, setSimulationResult] = useState<WorkflowDryRunResponse["data"] | null>(null);
  const [simulationContactId, setSimulationContactId] = useState<string>(WORKFLOW_DRY_RUN_CONTACT_ID);
  const [simulationContactLabel, setSimulationContactLabel] = useState("Mounted dry-run contact");
  const [showBacktest, setShowBacktest] = useState(false);
  const [backtestResult, setBacktestResult] = useState<WorkflowBacktestResponse["data"] | null>(null);
  const [canvasScale, setCanvasScale] = useState(1);
  const [canvasOffset, setCanvasOffset] = useState<CanvasPoint>({ x: 0, y: 0 });
  const { versions, isLoading: versionsLoading, refetch: refetchVersions } = useWorkflowVersions(workflowId, showVersions);

  const [dragState, setDragState] = useState<{
    nodeId: string;
    pointerId: number;
    nodeOrigin: CanvasPoint;
    pointerOrigin: CanvasPoint;
  } | null>(null);
  const [panState, setPanState] = useState<{
    pointerId: number;
    originOffset: CanvasPoint;
    pointerOrigin: { x: number; y: number };
  } | null>(null);

  const selectedNode = nodes.find((n) => n.id === selectedNodeId) ?? null;
  const publishWorkflowVersion = onPublish ?? defaultPublishWorkflowVersion;
  const runWorkflowDryRun = onDryRun === undefined ? defaultRunWorkflowDryRun : onDryRun;
  const runWorkflowBacktest = onBacktest === undefined ? defaultRunWorkflowBacktest : onBacktest;
  const supportsDryRun = runWorkflowDryRun !== null;
  const supportsBacktest = runWorkflowBacktest !== null;
  const nodeHitLookup = new Map(backtestResult?.nodeCounts.map((entry) => [entry.nodeId, entry.count]) ?? []);
  const branchCountLookup = new Map(backtestResult?.branchCounts.map((entry) => [entry.nodeId, entry.branches]) ?? []);
  const canvasCursor = dragState ? "grabbing" : panState ? "grabbing" : "grab";
  const renderedEdges = useMemo(() => edges.map((edge) => {
    const sourceNode = nodes.find((n) => n.id === edge.source);
    const targetNode = nodes.find((n) => n.id === edge.target);
    if (!sourceNode?.position || !targetNode?.position) {
      return null;
    }

    const source = {
      x: sourceNode.position.x + NODE_WIDTH,
      y: sourceNode.position.y + (NODE_HEIGHT / 2),
    };
    const target = {
      x: targetNode.position.x,
      y: targetNode.position.y + (NODE_HEIGHT / 2),
    };

    return {
      edge,
      path: buildBezierPath(source, target),
      labelX: (source.x + target.x) / 2,
      labelY: ((source.y + target.y) / 2) - 12,
    };
  }).filter(Boolean), [edges, nodes]);

  // Add a new node from palette
  const handleAddNode = useCallback((type: string) => {
    const id = `node_${Date.now()}`;
    const nodeType = NODE_TYPES.find((n) => n.type === type);
    const newNode: WorkflowNode = {
      id,
      type,
      config: { label: nodeType?.label ?? type },
      position: { x: 200 + Math.random() * 200, y: 100 + nodes.length * 100 },
    };
    setNodes((prev) => [...prev, newNode]);
    setSelectedNodeId(id);
    setDirty(true);
  }, [nodes.length]);

  // Update node config
  const handleUpdateNodeConfig = useCallback((config: Record<string, unknown>) => {
    if (!selectedNodeId) return;
    setNodes((prev) => prev.map((n) => n.id === selectedNodeId ? { ...n, config } : n));
    setDirty(true);
  }, [selectedNodeId]);

  // Delete selected node
  const handleDeleteNode = useCallback(() => {
    if (!selectedNodeId) return;
    setNodes((prev) => prev.filter((n) => n.id !== selectedNodeId));
    setEdges((prev) => prev.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId));
    setSelectedNodeId(null);
    setDirty(true);
  }, [selectedNodeId]);

  // Connect two nodes
  const handleNodeClick = useCallback((nodeId: string) => {
    if (connectingFrom) {
      if (connectingFrom !== nodeId) {
        const edgeId = `edge_${Date.now()}`;
        setEdges((prev) => [...prev, { id: edgeId, source: connectingFrom, target: nodeId }]);
        setDirty(true);
      }
      setConnectingFrom(null);
    } else {
      setSelectedNodeId(nodeId);
    }
  }, [connectingFrom]);

  // Save / publish version
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await publishWorkflowVersion(workflowId, nodes, edges);
      setDirty(false);
    } catch {
      // Error — stay in dirty state
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestoreVersion = async (versionId: string) => {
    setRestoringVersionId(versionId);
    try {
      await restoreWorkflowVersion(workflowId, versionId);
      await refetchVersions();
      const latest = await apiFetch<{ version: WorkflowVersion }>(`/crm/workflows/${workflowId}/versions/latest`);
      setNodes(latest.version?.nodes ?? []);
      setEdges(latest.version?.edges ?? []);
      setDirty(false);
      setTestResult({
        tone: "success",
        message: "Selected version restored into a new latest workflow version.",
      });
    } finally {
      setRestoringVersionId(null);
    }
  };

  const handleToggleTestMode = async () => {
    if (isTestMode) {
      setIsTestMode(false);
      setIsTesting(false);
      setTestResult(null);
      return;
    }

    setIsTestMode(true);

    if (isDirty) {
      setTestResult({
        tone: "error",
        message: "Save Version before running a dry run. Unsaved canvas changes are not part of the latest saved workflow version.",
      });
      return;
    }

    setIsTesting(true);
    setTestResult({
      tone: "info",
      message: "Running a dry run against the latest saved workflow version. No live actions are executed.",
    });

    try {
      if (!runWorkflowDryRun) {
        setTestResult({
          tone: "error",
          message: "Dry run is unavailable for this workflow editor instance.",
        });
        return;
      }

      const response = await runWorkflowDryRun(workflowId, WORKFLOW_DRY_RUN_CONTACT_ID);
      setTestResult({
        tone: "success",
        message: `Dry run completed against the latest saved version: ${response.estimatedPath.length} nodes, ${response.estimatedSteps.length} step(s), approx. ${formatEstimatedDuration(response.estimatedDurationMs)}.`,
      });
    } catch (error) {
      setTestResult({
        tone: "error",
        message: `Dry run failed: ${error instanceof Error ? error.message : "Unknown error"}`,
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleOpenSimulation = () => {
    setShowSimulation(true);
    setSimulationResult(null);
  };

  const handleOpenBacktest = () => {
    setShowBacktest(true);
  };

  const updateNodePosition = useCallback((nodeId: string, position: CanvasPoint) => {
    setNodes((prev) => prev.map((node) => node.id === nodeId ? {
      ...node,
      position: {
        x: Math.round(position.x),
        y: Math.round(position.y),
      },
    } : node));
  }, []);

  const handleCanvasWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    if (!canvasViewportRef.current) {
      return;
    }

    event.preventDefault();
    const rect = canvasViewportRef.current.getBoundingClientRect();
    const pointer = { x: event.clientX - rect.left, y: event.clientY - rect.top };

    if (event.ctrlKey || Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      setCanvasScale((currentScale) => {
        const nextScale = clampCanvasScale(currentScale - (event.deltaY * CANVAS_WHEEL_ZOOM_STEP));
        if (nextScale === currentScale) {
          return currentScale;
        }

        setCanvasOffset((currentOffset) => {
          const worldX = (pointer.x - currentOffset.x) / currentScale;
          const worldY = (pointer.y - currentOffset.y) / currentScale;
          return {
            x: pointer.x - (worldX * nextScale),
            y: pointer.y - (worldY * nextScale),
          };
        });

        return nextScale;
      });
      return;
    }

    setCanvasOffset((currentOffset) => ({
      x: currentOffset.x - event.deltaX,
      y: currentOffset.y - event.deltaY,
    }));
  }, []);

  const handleCanvasPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || event.target !== event.currentTarget) {
      return;
    }

    event.preventDefault();
    setSelectedNodeId(null);
    setConnectingFrom(null);
    setPanState({
      pointerId: event.pointerId,
      originOffset: canvasOffset,
      pointerOrigin: { x: event.clientX, y: event.clientY },
    });
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [canvasOffset]);

  const handleCanvasPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragState && event.pointerId === dragState.pointerId && canvasViewportRef.current) {
      const rect = canvasViewportRef.current.getBoundingClientRect();
      const pointer = getCanvasPoint(event.clientX, event.clientY, rect, canvasOffset, canvasScale);
      updateNodePosition(
        dragState.nodeId,
        computeDraggedNodePosition(dragState.nodeOrigin, dragState.pointerOrigin, pointer),
      );
      return;
    }

    if (panState && event.pointerId === panState.pointerId) {
      setCanvasOffset({
        x: panState.originOffset.x + (event.clientX - panState.pointerOrigin.x),
        y: panState.originOffset.y + (event.clientY - panState.pointerOrigin.y),
      });
    }
  }, [canvasOffset, canvasScale, dragState, panState, updateNodePosition]);

  const releasePointerInteraction = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragState && event.pointerId === dragState.pointerId) {
      setDragState(null);
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      return;
    }

    if (panState && event.pointerId === panState.pointerId) {
      setPanState(null);
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    }
  }, [dragState, panState]);

  const handleNodePointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>, node: WorkflowNode) => {
    if (!canvasViewportRef.current || event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const rect = canvasViewportRef.current.getBoundingClientRect();
    const pointer = getCanvasPoint(event.clientX, event.clientY, rect, canvasOffset, canvasScale);
    setDragState({
      nodeId: node.id,
      pointerId: event.pointerId,
      nodeOrigin: node.position ?? { x: 100, y: 100 },
      pointerOrigin: pointer,
    });
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [canvasOffset, canvasScale]);

  return (
    <div className="flex h-[calc(100vh-56px-48px)] flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-border bg-card px-4 py-2">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground">← Back</button>
          <h2 className="text-sm font-semibold text-foreground">{workflowName}</h2>
          {isDirty && <span className="text-xs text-yellow-600">Unsaved changes</span>}
          {/* Mode tabs */}
          <div className="ml-4 flex rounded-md border border-border">
            {([
              { mode: "visual" as const, icon: LayoutGrid, label: "Visual" },
              { mode: "ai" as const, icon: Sparkles, label: "AI" },
              { mode: "code" as const, icon: Code2, label: "Code" },
            ]).map(({ mode, icon: Icon, label }) => (
              <button
                key={mode}
                onClick={() => {
                  if (mode === "code" && editorMode !== "code") {
                    setCodeValue(JSON.stringify({ nodes, edges }, null, 2));
                  }
                  setEditorMode(mode);
                }}
                className={cn(
                  "flex items-center gap-1 px-3 py-1 text-xs font-medium transition-colors",
                  editorMode === mode
                    ? mode === "ai" ? "bg-[var(--color-ai)]/10 text-[var(--color-ai)]" : "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowVersions((current) => !current)}
            className={cn(
              "rounded-md border px-3 py-1 text-xs",
              showVersions ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground",
            )}
          >
            <span className="inline-flex items-center gap-1">
              <History className="h-3.5 w-3.5" />
              Versions
            </span>
          </button>
          <button
            onClick={() => setConnectingFrom(connectingFrom ? null : selectedNodeId)}
            className={cn(
              "rounded-md border px-3 py-1 text-xs",
              connectingFrom ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground",
            )}
          >
            {connectingFrom ? "Cancel Connect" : "🔗 Connect"}
          </button>
          {supportsDryRun ? (
            <button
              onClick={() => {
                void handleToggleTestMode();
              }}
              disabled={isTesting}
              className={cn(
                "rounded-md border px-3 py-1 text-xs disabled:opacity-60",
                isTestMode ? "border-[var(--color-success)] bg-[var(--color-success)]/5 text-[var(--color-success)]" : "border-border text-muted-foreground",
              )}
            >
              {isTesting ? "Running Test..." : isTestMode ? "Exit Test" : "🧪 Test"}
            </button>
          ) : null}
          {supportsDryRun ? (
            <button
              onClick={handleOpenSimulation}
              className="rounded-md border border-border px-3 py-1 text-xs text-muted-foreground"
            >
              Simulate Contact
            </button>
          ) : null}
          {supportsBacktest ? (
            <button
              onClick={handleOpenBacktest}
              className={cn(
                "rounded-md border px-3 py-1 text-xs",
                backtestResult ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground",
              )}
            >
              Simulate History
            </button>
          ) : null}
          <button
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            className="rounded-md bg-primary px-4 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50"
          >
            {isSaving ? "Saving..." : "Save Version"}
          </button>
        </div>
      </div>

      {/* Test mode result banner */}
      {testResult && (
        <div
          className={cn(
            "border-b px-4 py-2 text-xs",
            testResult.tone === "success" && "border-[var(--color-success)]/20 bg-[var(--color-success)]/5 text-[var(--color-success)]",
            testResult.tone === "error" && "border-red-200 bg-red-50 text-red-700",
            testResult.tone === "info" && "border-border bg-muted/40 text-muted-foreground",
          )}
        >
          {testResult.message}
        </div>
      )}

      {editorMode === "visual" && compliance?.checks?.length ? (
        <div className="border-b border-border bg-muted/20 px-4 py-3">
          <WorkflowCompliancePanel
            compliance={compliance}
            quietHours={quietHours}
            frequencyCap={frequencyCap}
          />
        </div>
      ) : null}

      {/* Main area — mode-dependent */}
      {editorMode === "ai" && (
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="w-full max-w-2xl space-y-4">
            <div className="rounded-xl border-2 border-dashed border-[var(--color-ai)]/30 bg-[var(--color-ai)]/5 p-6">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-5 w-5 text-[var(--color-ai)]" />
                <h3 className="text-sm font-semibold text-foreground">Describe your workflow</h3>
                <span className="ml-auto rounded-full bg-[var(--color-ai)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--color-ai)]">AI-generated · Review before saving</span>
              </div>
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="e.g., When a lead fills out the contact form, send a welcome email, wait 2 days, then create a follow-up task for sales."
                className="w-full rounded-lg border border-border bg-background p-3 text-sm text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-[var(--color-ai)]/30 focus:border-[var(--color-ai)]"
                rows={4}
              />
              <div className="mt-3 flex items-center gap-3">
                <button
                  onClick={async () => {
                    if (!aiPrompt.trim()) return;
                    setAiLoading(true);
                    setAiError(null);
                    try {
                      const response = await apiFetch<WorkflowAiGenerateResponse>("/crm/ai/complete", {
                        method: "POST",
                        body: JSON.stringify({
                          prompt: aiPrompt,
                          featureKey: "crm.assistant.complete",
                          messages: [
                            {
                              role: "system",
                              content: buildCanvasWorkflowPrompt(NODE_TYPES),
                            },
                            {
                              role: "user",
                              content: aiPrompt,
                            },
                          ],
                        }),
                      });
                      const content = typeof response.content === "string" ? response.content : "";
                      const generated = parseGeneratedCanvasWorkflow(content, NODE_TYPES);
                      setNodes(generated.nodes);
                      setEdges(generated.edges);
                      setDirty(true);
                      setEditorMode("visual");
                    } catch (error) {
                      setAiError(error instanceof Error ? error.message : "Could not generate a valid workflow.");
                    } finally {
                      setAiLoading(false);
                    }
                  }}
                  disabled={aiLoading || !aiPrompt.trim()}
                  className="btn-press rounded-lg px-5 py-2 text-sm font-medium text-white disabled:opacity-50"
                  style={{ background: "var(--color-ai)" }}
                >
                  {aiLoading ? "Generating..." : "Generate Workflow"}
                </button>
                <p className="text-xs text-muted-foreground">You can edit the result before saving</p>
              </div>
              {aiError && (
                <p className="mt-3 text-xs text-[var(--color-ai)]">{aiError}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {editorMode === "code" && (
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="border-b border-border px-4 py-2 text-xs text-muted-foreground">
            Edit workflow JSON directly. Changes sync to visual mode when you switch back.
          </div>
          <div className="flex-1 overflow-auto p-4">
            <textarea
              value={codeValue}
              onChange={(e) => setCodeValue(e.target.value)}
              className="h-full w-full rounded-lg border border-border bg-background p-4 font-mono text-xs text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
              spellCheck={false}
            />
          </div>
          <div className="flex items-center gap-3 border-t border-border px-4 py-2">
            <button
              onClick={() => {
                try {
                  const parsed = JSON.parse(codeValue);
                  if (Array.isArray(parsed.nodes) && Array.isArray(parsed.edges)) {
                    setNodes(parsed.nodes);
                    setEdges(parsed.edges);
                    setDirty(true);
                    setEditorMode("visual");
                  }
                } catch {
                  // Invalid JSON — stay in code mode
                }
              }}
              className="rounded-md bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground"
            >
              Apply & Switch to Visual
            </button>
            <span className="text-xs text-muted-foreground">
              {(() => { try { JSON.parse(codeValue); return "✓ Valid JSON"; } catch { return "✗ Invalid JSON"; } })()}
            </span>
          </div>
        </div>
      )}

      {editorMode === "visual" && (
      <div className="flex flex-1 overflow-hidden">
        <NodePalette onAddNode={handleAddNode} />

        {/* Canvas */}
        <div
          ref={canvasViewportRef}
          className="relative flex-1 overflow-hidden bg-[var(--color-muted)]/30"
          data-testid="workflow-canvas-viewport"
          onClick={() => { setSelectedNodeId(null); setConnectingFrom(null); }}
          onWheel={handleCanvasWheel}
          onPointerDown={handleCanvasPointerDown}
          onPointerMove={handleCanvasPointerMove}
          onPointerUp={releasePointerInteraction}
          onPointerCancel={releasePointerInteraction}
          style={{ cursor: canvasCursor }}
        >
          <div
            className="absolute left-4 top-4 z-10 rounded-full border border-border bg-background/90 px-3 py-1 text-[11px] font-medium text-muted-foreground shadow-sm backdrop-blur"
            data-testid="workflow-canvas-scale-indicator"
          >
            {Math.round(canvasScale * 100)}%
          </div>

          <div
            className="absolute inset-0"
            style={{
              transform: `translate(${canvasOffset.x}px, ${canvasOffset.y}px) scale(${canvasScale})`,
              transformOrigin: "0 0",
            }}
          >

          {/* SVG edges */}
          <svg className="absolute inset-0 h-full w-full pointer-events-none overflow-visible" data-testid="workflow-canvas-edges">
            {renderedEdges.map((rendered) => {
              if (!rendered) {
                return null;
              }

              return (
                <g key={rendered.edge.id}>
                  <path
                    d={rendered.path}
                    stroke="currentColor"
                    strokeWidth={2}
                    fill="none"
                    className="text-border"
                    markerEnd="url(#workflow-arrowhead)"
                    data-testid={`workflow-edge-${rendered.edge.id}`}
                  />
                  {rendered.edge.label ? (
                    <text
                      x={rendered.labelX}
                      y={rendered.labelY}
                      textAnchor="middle"
                      className="fill-muted-foreground text-[10px]"
                    >
                      {rendered.edge.label}
                    </text>
                  ) : null}
                </g>
              );
            })}
            <defs>
              <marker id="workflow-arrowhead" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" className="fill-border" />
              </marker>
            </defs>
          </svg>

          {/* Nodes */}
          {nodes.map((node) => {
            const nodeType = NODE_TYPES.find((n) => n.type === node.type);
            const categoryColor = nodeType?.category === "trigger"
              ? "border-[var(--color-secondary)]/30 bg-[var(--color-secondary)]/5 dark:bg-[var(--color-secondary)]/30"
              : nodeType?.category === "condition"
                ? "border-yellow-300 bg-[var(--color-warning)]/5 dark:bg-yellow-950/30"
                : "border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5 dark:bg-[var(--color-primary)]/30";
            const nodeHitCount = nodeHitLookup.get(node.id) ?? 0;
            const branchCounts = branchCountLookup.get(node.id) ?? [];

            return (
              <div
                key={node.id}
                onClick={(e) => { e.stopPropagation(); handleNodeClick(node.id); }}
                onPointerDown={(event) => handleNodePointerDown(event, node)}
                onPointerMove={handleCanvasPointerMove}
                onPointerUp={releasePointerInteraction}
                onPointerCancel={releasePointerInteraction}
                className={cn(
                  "absolute flex w-40 cursor-pointer items-center gap-2 rounded-lg border-2 px-3 py-2 shadow-sm transition-all",
                  categoryColor,
                  selectedNodeId === node.id && "ring-2 ring-primary",
                  connectingFrom === node.id && "ring-2 ring-green-500",
                )}
                style={{
                  transform: `translate(${node.position?.x ?? 100}px, ${node.position?.y ?? 100}px)`,
                }}
                data-testid={`workflow-node-${node.id}`}
              >
                {backtestResult ? (
                  <div className="absolute -right-2 -top-2 rounded-full border border-primary/20 bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground shadow-sm">
                    {nodeHitCount}
                  </div>
                ) : null}
                <span className="text-base">{nodeType?.icon ?? "📌"}</span>
                <div className="flex-1 overflow-hidden">
                  <p className="truncate text-xs font-medium">
                    {String(node.config.label ?? nodeType?.label ?? node.type)}
                  </p>
                  {backtestResult && branchCounts.length > 0 ? (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {branchCounts.map((branch) => (
                        <span
                          key={`${node.id}-${branch.targetNodeId}`}
                          className="rounded-full border border-border bg-background/80 px-1.5 py-0.5 text-[10px] text-muted-foreground"
                        >
                          {branch.label}: {branch.count}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}

          {nodes.length === 0 && (
            <div className="flex h-full items-center justify-center">
              <p className="text-sm text-muted-foreground">
                Add nodes from the palette on the left to build your workflow
              </p>
            </div>
          )}
          </div>
        </div>

        {/* Config panel */}
        {selectedNode && (
          <NodeConfig
            node={selectedNode}
            onUpdate={handleUpdateNodeConfig}
            onDelete={handleDeleteNode}
            onClose={() => setSelectedNodeId(null)}
          />
        )}

        {showVersions && (
          <VersionsDrawer
            versions={versions}
            isLoading={versionsLoading}
            isDirty={isDirty}
            restoringVersionId={restoringVersionId}
            onRestore={handleRestoreVersion}
            onClose={() => setShowVersions(false)}
          />
        )}
        {showSimulation && supportsDryRun ? (
          <SimulationDrawer
            workflowId={workflowId}
            selectedContactId={simulationContactId}
            selectedContactLabel={simulationContactLabel}
            result={simulationResult}
            isDirty={isDirty}
            onRun={async (contactId, label) => {
              setSimulationContactId(contactId);
              setSimulationContactLabel(label);
              const result = await runWorkflowDryRun(workflowId, contactId);
              setSimulationResult(result);
            }}
            onClose={() => setShowSimulation(false)}
          />
        ) : null}
        {showBacktest && supportsBacktest ? (
          <BacktestDrawer
            workflowId={workflowId}
            result={backtestResult}
            isDirty={isDirty}
            onRun={async (range) => {
              const result = await runWorkflowBacktest(workflowId, range);
              setBacktestResult(result);
            }}
            onClose={() => setShowBacktest(false)}
          />
        ) : null}
      </div>
      )}
    </div>
  );
}

function VersionsDrawer({
  versions,
  isLoading,
  isDirty,
  restoringVersionId,
  onRestore,
  onClose,
}: {
  versions: WorkflowVersion[];
  isLoading: boolean;
  isDirty: boolean;
  restoringVersionId: string | null;
  onRestore: (versionId: string) => void;
  onClose: () => void;
}) {
  const latestVersion = versions.find((version) => version.isCurrent) ?? versions[0] ?? null;
  const [compareVersionId, setCompareVersionId] = useState<string | null>(null);
  const compareVersion = useMemo(() => {
    if (!latestVersion) {
      return null;
    }

    return (
      versions.find((version) => version.id === compareVersionId) ??
      versions.find((version) => version.id !== latestVersion.id) ??
      null
    );
  }, [compareVersionId, latestVersion, versions]);
  const comparison = useMemo(() => {
    if (!latestVersion || !compareVersion || latestVersion.id === compareVersion.id) {
      return null;
    }

    const compareSnapshot = toWorkflowVersionSnapshot(compareVersion);
    const latestSnapshot = toWorkflowVersionSnapshot(latestVersion);
    if (!compareSnapshot || !latestSnapshot) {
      return null;
    }

    return diffWorkflowVersions(compareSnapshot, latestSnapshot);
  }, [compareVersion, latestVersion]);
  const projectedOutcomeChanges = useMemo(
    () => comparison ? buildProjectedOutcomeChanges(comparison) : [],
    [comparison],
  );
  const addedNodeItems = comparison?.nodes.added.map((node) => node.id) ?? [];
  const removedNodeItems = comparison?.nodes.removed.map((node) => node.id) ?? [];
  const changedNodeItems = comparison?.nodes.changed.map(
    (node) => `${node.id} (${node.changedFields.join(", ")})`,
  ) ?? [];
  const addedEdgeItems = comparison?.edges.added.map((edge) => edge.id) ?? [];
  const removedEdgeItems = comparison?.edges.removed.map((edge) => edge.id) ?? [];
  const changedEdgeItems = comparison?.edges.changed.map(
    (edge) => `${edge.id} (${edge.changedFields.join(", ")})`,
  ) ?? [];

  return (
    <div className="w-[28rem] shrink-0 border-l border-border bg-card p-4">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Versions</h3>
          <p className="text-xs text-muted-foreground">Restore a previous version into a new latest draft.</p>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
      </div>

      {isDirty ? (
        <div className="mb-4 rounded-md border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 p-3 text-xs text-[var(--color-warning)]">
          Unsaved canvas edits are excluded here. Version compare stays anchored to saved workflow versions only.
        </div>
      ) : null}

      {isLoading ? (
        <div className="space-y-2 text-xs text-muted-foreground">Loading versions…</div>
      ) : versions.length === 0 ? (
        <p className="text-xs text-muted-foreground">No saved versions yet.</p>
      ) : (
        <div className="space-y-4">
          {latestVersion && compareVersion ? (
            <div className="rounded-lg border border-border bg-background p-3">
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Compare saved versions</p>
                <p className="text-sm font-medium text-foreground">
                  Version {compareVersion.version} {"->"} Version {latestVersion.version}
                </p>
                <p className="text-xs text-muted-foreground">
                  Structural projection only. Use Simulate Contact or Simulate History to confirm runtime impact.
                </p>
              </div>

              {comparison ? (
                <>
                  <div className="mt-3 grid grid-cols-2 gap-2" data-testid="workflow-version-diff">
                    <Metric
                      label="Steps"
                      value={formatVersionDelta(
                        comparison.nodes.added.length,
                        comparison.nodes.removed.length,
                        comparison.nodes.changed.length,
                      )}
                      icon={<Waypoints className="h-3.5 w-3.5" />}
                    />
                    <Metric
                      label="Paths"
                      value={formatVersionDelta(
                        comparison.edges.added.length,
                        comparison.edges.removed.length,
                        comparison.edges.changed.length,
                      )}
                      icon={<GitBranch className="h-3.5 w-3.5" />}
                    />
                  </div>

                  <div className="mt-4">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                      Projected outcome changes
                    </p>
                    <div className="space-y-2">
                      {projectedOutcomeChanges.length > 0 ? (
                        projectedOutcomeChanges.map((message, index) => (
                          <div
                            key={`${message}-${index}`}
                            className="rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground"
                          >
                            {message}
                          </div>
                        ))
                      ) : (
                        <div className="rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
                          No structural delta. Saved projections should remain unchanged.
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 space-y-4">
                    <CompareListSection title="Added steps" items={addedNodeItems} />
                    <CompareListSection title="Removed steps" items={removedNodeItems} />
                    <CompareListSection title="Changed steps" items={changedNodeItems} />
                    <CompareListSection title="Added paths" items={addedEdgeItems} />
                    <CompareListSection title="Removed paths" items={removedEdgeItems} />
                    <CompareListSection title="Changed paths" items={changedEdgeItems} />
                  </div>
                </>
              ) : (
                <div className="mt-3 rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
                  Structural compare is unavailable until this saved version includes nodes and edges in the mounted versions payload.
                </div>
              )}
            </div>
          ) : null}

          {versions.map((version) => (
            <div key={version.id} className="rounded-lg border border-border p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground">Version {version.version}</p>
                    {version.isCurrent ? (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary">Current</span>
                    ) : null}
                  </div>
                  <p className="text-xs text-muted-foreground">{new Date(version.publishedAt).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">
                    {version.nodeCount ?? version.nodes?.length ?? 0} nodes · {version.edgeCount ?? version.edges?.length ?? 0} edges
                  </p>
                  {version.changeSummary ? (
                    <p className="text-xs text-muted-foreground">{version.changeSummary}</p>
                  ) : null}
                </div>
                <div className="flex flex-col items-end gap-2">
                  {latestVersion && latestVersion.id !== version.id ? (
                    <button
                      type="button"
                      onClick={() => setCompareVersionId(version.id)}
                      className={cn(
                        "rounded-md border px-3 py-1 text-xs hover:bg-accent",
                        compareVersion?.id === version.id
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border text-muted-foreground",
                      )}
                    >
                      {compareVersion?.id === version.id ? "Comparing" : `Compare to v${latestVersion.version}`}
                    </button>
                  ) : (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary">Latest baseline</span>
                  )}
                  <button
                    onClick={() => onRestore(version.id)}
                    disabled={Boolean(version.isCurrent) || restoringVersionId === version.id}
                    className="rounded-md border border-border px-3 py-1 text-xs text-muted-foreground hover:bg-accent disabled:opacity-50"
                  >
                    {restoringVersionId === version.id ? "Restoring..." : "Restore"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SimulationDrawer({
  workflowId,
  selectedContactId,
  selectedContactLabel,
  result,
  isDirty,
  onRun,
  onClose,
}: {
  workflowId: string;
  selectedContactId: string;
  selectedContactLabel: string;
  result: WorkflowDryRunResponse["data"] | null;
  isDirty: boolean;
  onRun: (contactId: string, label: string) => Promise<void>;
  onClose: () => void;
}) {
  const [search, setSearch] = useState("");
  const [pendingContactId, setPendingContactId] = useState(selectedContactId);
  const [pendingLabel, setPendingLabel] = useState(selectedContactLabel);
  const [isRunning, setIsRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const { contacts, isLoading } = useContactList({ search, limit: 8 });

  const runSimulation = async () => {
    setIsRunning(true);
    setRunError(null);
    try {
      await onRun(pendingContactId, pendingLabel);
    } catch (error) {
      setRunError(error instanceof Error ? error.message : "Simulation failed");
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="w-[28rem] shrink-0 border-l border-border bg-card p-4">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Contact Simulation</h3>
          <p className="text-xs text-muted-foreground">Run the mounted dry-run against a real CRM contact.</p>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
      </div>

      {isDirty ? (
        <div className="mb-4 rounded-md border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 p-3 text-xs text-[var(--color-warning)]">
          Save Version first. Simulation runs against the latest saved workflow version, not unsaved canvas changes.
        </div>
      ) : null}

      <div className="space-y-3">
        <label className="relative block">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search contacts"
            className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm"
          />
        </label>

        <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-border p-2">
          <button
            onClick={() => {
              setPendingContactId(WORKFLOW_DRY_RUN_CONTACT_ID);
              setPendingLabel("Mounted dry-run contact");
            }}
            className={cn(
              "flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-accent/40",
              pendingContactId === WORKFLOW_DRY_RUN_CONTACT_ID && "bg-primary/10 text-primary",
            )}
          >
            <span>Mounted dry-run contact</span>
            <span className="text-xs text-muted-foreground">Default</span>
          </button>
          {isLoading ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">Loading contacts…</p>
          ) : contacts.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">No matching contacts.</p>
          ) : (
            contacts.map((contact) => {
              const label = [contact.firstName, contact.lastName].filter(Boolean).join(" ") || contact.email || contact.id;
              return (
                <button
                  key={contact.id}
                  onClick={() => {
                    setPendingContactId(contact.id);
                    setPendingLabel(label);
                  }}
                  className={cn(
                    "flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-accent/40",
                    pendingContactId === contact.id && "bg-primary/10 text-primary",
                  )}
                >
                  <div>
                    <p>{label}</p>
                    <p className="text-xs text-muted-foreground">{contact.email ?? contact.source ?? "CRM contact"}</p>
                  </div>
                  <UserRound className="h-4 w-4" />
                </button>
              );
            })
          )}
        </div>

        <button
          onClick={() => { void runSimulation(); }}
          disabled={isDirty || isRunning}
          className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {isRunning ? "Simulating..." : `Simulate ${pendingLabel}`}
        </button>
        {runError ? <p className="text-xs text-destructive">{runError}</p> : null}
      </div>

      {result ? (
        <div className="mt-5 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <Metric label="Path nodes" value={String(result.estimatedPath.length)} icon={<Waypoints className="h-3.5 w-3.5" />} />
            <Metric label="Duration" value={formatEstimatedDuration(result.estimatedDurationMs)} icon={<Clock3 className="h-3.5 w-3.5" />} />
            <Metric label="Branches" value={String(result.branchDecisions.length)} icon={<GitBranch className="h-3.5 w-3.5" />} />
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Timeline</p>
            <div className="space-y-2">
              {result.estimatedSteps.map((step, index) => (
                <div key={`${step.nodeId}-${index}`} className="rounded-md border border-border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-foreground">{step.nodeType.replace(/_/g, " ")}</p>
                    <span className="text-xs text-muted-foreground">{formatEstimatedDuration(step.estimatedDurationMs)}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{step.nodeId}</p>
                  {step.actionRef ? <p className="mt-1 text-xs text-muted-foreground">Action: {step.actionRef}</p> : null}
                  {step.reason ? <p className="mt-1 text-xs text-muted-foreground">{step.reason}</p> : null}
                </div>
              ))}
            </div>
          </div>

          {result.branchDecisions.length > 0 ? (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Branch decisions</p>
              <div className="space-y-2">
                {result.branchDecisions.map((decision, index) => (
                  <div key={`${decision.nodeId}-${index}`} className="rounded-md border border-border p-3 text-xs text-muted-foreground">
                    <p className="font-medium text-foreground">{decision.nodeId}</p>
                    <p>Condition: {decision.condition}</p>
                    <p>Branch: {decision.branch}</p>
                    <p>{decision.reason}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {result.warnings.length > 0 ? (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Warnings</p>
              <div className="space-y-2">
                {result.warnings.map((warning, index) => (
                  <div key={`${warning}-${index}`} className="rounded-md border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 p-3 text-xs text-[var(--color-warning)]">
                    {warning}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <p className="mt-4 text-[11px] text-muted-foreground">
        Workflow ID: {workflowId}. Historical backtest overlays are still a separate runtime slice; this panel is the real contact-level dry-run.
      </p>
    </div>
  );
}

function BacktestDrawer({
  workflowId,
  result,
  isDirty,
  onRun,
  onClose,
}: {
  workflowId: string;
  result: WorkflowBacktestResponse["data"] | null;
  isDirty: boolean;
  onRun: (range: "7d" | "30d" | "90d") => Promise<void>;
  onClose: () => void;
}) {
  const [range, setRange] = useState<"7d" | "30d" | "90d">(result?.range ?? "30d");
  const [isRunning, setIsRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);

  const runBacktest = async () => {
    setIsRunning(true);
    setRunError(null);
    try {
      await onRun(range);
    } catch (error) {
      setRunError(error instanceof Error ? error.message : "Backtest failed");
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="w-[28rem] shrink-0 border-l border-border bg-card p-4">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Historical Backtest</h3>
          <p className="text-xs text-muted-foreground">Overlay real workflow execution counts across the selected date range.</p>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
      </div>

      {isDirty ? (
        <div className="mb-4 rounded-md border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 p-3 text-xs text-[var(--color-warning)]">
          Save Version first. Historical backtest is anchored to the latest saved workflow version so canvas nodes and execution aggregates stay comparable.
        </div>
      ) : null}

      <div className="space-y-3">
        <div className="flex gap-2">
          {(["7d", "30d", "90d"] as const).map((option) => (
            <button
              key={option}
              onClick={() => setRange(option)}
              className={cn(
                "rounded-md border px-3 py-1.5 text-xs font-medium",
                range === option ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground",
              )}
            >
              {option}
            </button>
          ))}
        </div>

        <button
          onClick={() => { void runBacktest(); }}
          disabled={isDirty || isRunning}
          className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {isRunning ? "Running backtest..." : `Run ${range} backtest`}
        </button>
        {runError ? <p className="text-xs text-destructive">{runError}</p> : null}
      </div>

      {result ? (
        <div className="mt-5 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <Metric label="Enrollments" value={String(result.totalEnrollments)} icon={<History className="h-3.5 w-3.5" />} />
            <Metric label="Triggered" value={String(result.triggeredEnrollments)} icon={<Waypoints className="h-3.5 w-3.5" />} />
            <Metric label="Completed" value={String(result.completedEnrollments)} icon={<Clock3 className="h-3.5 w-3.5" />} />
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Node coverage</p>
            <div className="space-y-2">
              {result.nodeCounts.map((node) => (
                <div key={node.nodeId} className="rounded-md border border-border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-foreground">{node.nodeName}</p>
                    <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
                      {node.count} hit{node.count === 1 ? "" : "s"}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{node.nodeId}</p>
                </div>
              ))}
            </div>
          </div>

          {result.branchCounts.length > 0 ? (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Branch counts</p>
              <div className="space-y-2">
                {result.branchCounts.map((entry) => (
                  <div key={entry.nodeId} className="rounded-md border border-border p-3">
                    <p className="text-sm font-medium text-foreground">{entry.nodeId}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {entry.branches.map((branch) => (
                        <span
                          key={`${entry.nodeId}-${branch.targetNodeId}`}
                          className="rounded-full border border-border bg-background px-2 py-0.5 text-xs text-muted-foreground"
                        >
                          {branch.label}: {branch.count}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <p className="mt-4 text-[11px] text-muted-foreground">
        Workflow ID: {workflowId}. This backtest uses real tenant execution history inside the selected window, not a mocked canvas overlay.
      </p>
    </div>
  );
}

function Metric({ label, value, icon }: { label: string; value: string; icon: ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-background px-3 py-2">
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <p className="mt-1 text-sm font-semibold text-foreground">{value}</p>
    </div>
  );
}

function CompareListSection({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) {
    return null;
  }

  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">{title}</p>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div
            key={`${title}-${item}-${index}`}
            className="rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground"
          >
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}
